import { useState } from "react";
import { X, Loader2, CheckCircle2 } from "lucide-react";
import { adicionarEspera } from "@/lib/participantes";
import { mascaraCPF, mascaraTelefone, apenasDigitos } from "@/lib/masks";

interface Props {
  edicaoId: string;
  edicaoNome: string;
  cidade: string;
  onClose: () => void;
}

export default function WaitlistModal({ edicaoId, edicaoNome, cidade, onClose }: Props) {
  const [nome, setNome] = useState("");
  const [cpf, setCpf] = useState("");
  const [email, setEmail] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [loading, setLoading] = useState(false);
  const [sucesso, setSucesso] = useState(false);
  const [erro, setErro] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErro("");

    const cpfLimpo = apenasDigitos(cpf);
    const wppLimpo = apenasDigitos(whatsapp);

    if (cpfLimpo.length !== 11) {
      setErro("CPF inválido. Informe os 11 dígitos.");
      return;
    }
    if (wppLimpo.length < 10) {
      setErro("WhatsApp inválido. Informe DDD + número.");
      return;
    }

    setLoading(true);
    try {
      await adicionarEspera(edicaoId, {
        nomeCompleto: nome.trim(),
        cpf: cpfLimpo,
        email: email.trim(),
        whatsapp: wppLimpo,
      });

      const primeiroNome = nome.trim().split(" ")[0];
      const mensagemBoas = `Olá ${primeiroNome}, você entrou para a lista de espera para o DU NADA - ${cidade} 🎟️\n\nFique atento ao seu WhatsApp e redes sociais para não perder nenhuma atualização! 🔥`;
      fetch("/api/zapi/send", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: wppLimpo, type: "texto", message: mensagemBoas }),
      }).catch(() => {});

      setSucesso(true);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Erro ao realizar cadastro.";
      setErro(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative w-full max-w-md z-10" style={{ backgroundColor: "#F8EBC1" }}>
        {/* Header */}
        <div className="flex items-start justify-between p-8 pb-0">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase mb-1" style={{ color: "#3A282466" }}>
              Lista de Espera
            </p>
            <h2
              className="text-2xl font-bold uppercase tracking-tight"
              style={{ fontFamily: "var(--font-oswald)", color: "#3A2824" }}
            >
              {edicaoNome}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="transition mt-1 hover:opacity-60"
            style={{ color: "#3A282466" }}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {sucesso ? (
          <div className="p-8 flex flex-col items-center gap-4 text-center">
            <CheckCircle2 className="w-12 h-12" style={{ color: "#3A2824" }} />
            <div>
              <p
                className="text-xl font-bold uppercase tracking-tight"
                style={{ fontFamily: "var(--font-oswald)", color: "#3A2824" }}
              >
                Cadastro realizado!
              </p>
              <p className="text-sm mt-1" style={{ color: "#3A282480" }}>
                Você está na lista de espera. Entraremos em contato via WhatsApp.
              </p>
            </div>
            <button
              onClick={onClose}
              className="mt-2 w-full py-3 text-sm font-medium tracking-widest uppercase transition hover:opacity-90"
              style={{ backgroundColor: "#3A2824", color: "#F8EBC1" }}
            >
              Fechar
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-8 space-y-4">
            <div className="space-y-1">
              <label className="text-xs tracking-widest uppercase" style={{ color: "#3A282866" }}>
                Nome completo
              </label>
              <input
                required
                type="text"
                value={nome}
                onChange={(e) => {
                  const valor = e.target.value;
                  // Remove números do valor
                  const valorSemNumeros = valor.replace(/[0-9]/g, '');
                  setNome(valorSemNumeros);
                }}
                placeholder="Seu nome"
                className="w-full border-b py-2.5 text-sm outline-none transition bg-transparent"
                style={{ 
                  borderColor: "#3A282433",
                  color: "#3A2824"
                }}
                onFocus={(e) => e.target.style.borderColor = "#3A2824"}
                onBlur={(e) => e.target.style.borderColor = "#3A282433"}
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs tracking-widest uppercase" style={{ color: "#3A282866" }}>
                CPF
              </label>
              <input
                required
                type="text"
                value={cpf}
                onChange={(e) => setCpf(mascaraCPF(e.target.value))}
                placeholder="000.000.000-00"
                maxLength={14}
                className="w-full border-b py-2.5 text-sm outline-none transition bg-transparent"
                style={{ 
                  borderColor: "#3A282433",
                  color: "#3A2824"
                }}
                onFocus={(e) => e.target.style.borderColor = "#3A2824"}
                onBlur={(e) => e.target.style.borderColor = "#3A282433"}
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs tracking-widest uppercase" style={{ color: "#3A282866" }}>
                E-mail
              </label>
              <input
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu@email.com"
                className="w-full border-b py-2.5 text-sm outline-none transition bg-transparent"
                style={{ 
                  borderColor: "#3A282433",
                  color: "#3A2824"
                }}
                onFocus={(e) => e.target.style.borderColor = "#3A2824"}
                onBlur={(e) => e.target.style.borderColor = "#3A282433"}
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs tracking-widest uppercase" style={{ color: "#3A282866" }}>
                WhatsApp
              </label>
              <input
                required
                type="text"
                value={whatsapp}
                onChange={(e) => setWhatsapp(mascaraTelefone(e.target.value))}
                placeholder="(11) 99999-9999"
                maxLength={16}
                className="w-full border-b py-2.5 text-sm outline-none transition bg-transparent"
                style={{ 
                  borderColor: "#3A282433",
                  color: "#3A2824"
                }}
                onFocus={(e) => e.target.style.borderColor = "#3A2824"}
                onBlur={(e) => e.target.style.borderColor = "#3A282433"}
              />
            </div>

            {erro && (
              <p className="text-xs text-red-500">{erro}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 text-sm font-medium tracking-widest uppercase transition disabled:opacity-50 flex items-center justify-center gap-2 mt-2 hover:opacity-90"
              style={{ backgroundColor: "#3A2824", color: "#F8EBC1" }}
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Cadastrando...</>
              ) : (
                "Entrar na lista"
              )}
            </button>

            <p className="text-xs text-center" style={{ color: "#3A282866" }}>
              Seus dados são usados apenas para contato sobre o evento.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
